/**
 * Le point d'un moment de la séance, chacun sa signature — le même dans la tuile (`<TutorSessionThread>`) et dans
 * l'onglet « Fil » du centre (`<TutorSessionReplay>`) : un rendu juste (vert) ou faux (rouge), pas encore corrigé
 * (cerclé de vert), une main (orange d'alerte), un silence (violet), le champion (son bleu), un quiz (marine), ce que le
 * masteur a donné (la couleur de l'univers), en gris plein ce qui est tombé sans décision, l'entrée dans la salle (l'encre).
 *
 * Trois signatures n'existent que dans le fil du centre, qui dit aussi ce que la tuile tait : `trace` (un fait sans
 * signature, un petit point au contour), `waiting` (ce qui attend encore : cerclé à la couleur de l'univers), `away`
 * (une sortie de l'appli : cerclé de gris).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorRibbonEventKind } from "../../../ports/primary/ui/types/tutorLive"
import { type SemanticColorKey } from "../../theme/tokens/colors"

export type ThreadDotKind = UITutorRibbonEventKind | "trace" | "waiting" | "away"

const DOT_COLOR: Record<ThreadDotKind, SemanticColorKey> = {
    ok: "success",
    ko: "danger",
    done: "success",
    hand: "warning",
    silence: "purple",
    helper: "champion-sam",
    quiz: "info",
    sent: "role",
    stale: "text-muted",
    error: "danger",
    join: "text-primary",
    trace: "border",
    waiting: "role",
    away: "text-muted",
}

/** La couleur d'un mode sur la piste, la même dans la tuile et dans l'onglet « Fil ». */
export const MODE_COLOR: Record<"dialogue" | "focused", SemanticColorKey> = { dialogue: "success", focused: "purple" }

/** Cerclé, sans fond : ce qui n'est pas encore tranché, ou une absence. */
const RINGED = new Set<ThreadDotKind>(["done", "waiting", "away"])

export type ThreadDotProps = {
    kind: ThreadDotKind
    size: number
    /** Plusieurs moments fondus en un point (la tuile) : un anneau marine le dit. */
    merged?: boolean
}

export function ThreadDot(props: ThreadDotProps) {
    const { kind, size, merged = false } = props
    return <View style={styles.dot(kind, kind === "trace" ? size - 2 : size, merged)} />
}

const styles = StyleSheet.create((theme) => ({
    dot: (kind: ThreadDotKind, size: number, merged: boolean) => ({
        width: merged ? size + 2 : size,
        height: merged ? size + 2 : size,
        borderRadius: theme.radius.full,
        backgroundColor: RINGED.has(kind) ? theme.colors.surface : theme.colors[DOT_COLOR[kind]],
        borderWidth: RINGED.has(kind) || merged ? 2 : 1,
        borderColor: RINGED.has(kind)
            ? theme.colors[DOT_COLOR[kind]]
            : merged
              ? theme.colors["text-primary"]
              : theme.colors.surface,
    }),
}))
